import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Chip from '@material-ui/core/Chip';
import PageTitle from '../../Utils/smsTitle';
import { SubHeader } from '../../../Containers/SubHeader';

const useStyles = makeStyles(() => ({
  root: { margin: 22, padding: 20 },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  filterLabel: { fontSize: '0.85rem', fontWeight: 500, color: '#555' },
  filterSelect: { minWidth: 160 },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: 12,
    marginTop: 24,
    color: '#333',
  },
  sectionPaper: { padding: 16, marginBottom: 20 },
  tableHead: {
    backgroundColor: '#f5f5f5',
    '& th': { fontWeight: 600, fontSize: '0.8rem', color: '#333' },
  },
  tableCell: { fontSize: '0.85rem' },
  utilizationBarBg: {
    width: '100%',
    height: 18,
    backgroundColor: '#e0e0e0',
    borderRadius: 9,
    overflow: 'hidden',
    position: 'relative',
  },
  utilizationBarFill: {
    height: '100%',
    borderRadius: 9,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: 6,
    fontSize: '0.65rem',
    fontWeight: 700,
    color: '#fff',
    minWidth: 28,
    transition: 'width 0.4s ease',
  },
  summaryCard: {
    padding: 20,
    borderRadius: 8,
    textAlign: 'center',
    color: '#fff',
    minHeight: 90,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  summaryValue: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.2 },
  summaryLabel: { fontSize: '0.8rem', marginTop: 4, opacity: 0.9 },
  peakSlot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    borderBottom: '1px solid #f0f0f0',
  },
  peakTime: { fontSize: '0.85rem', fontWeight: 600, color: '#333' },
  peakCount: { fontSize: '0.85rem', color: '#555' },
  cancellationRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    padding: '12px 0',
  },
  cancellationStat: { textAlign: 'center' },
  cancellationValue: { fontSize: '1.25rem', fontWeight: 700, color: '#F44336' },
  cancellationLabel: { fontSize: '0.75rem', color: '#777' },
}));

// ── Mock Data ────────────────────────────────────────────────────────────────
const DATE_RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

const THEATRE_DATA = [
  {
    name: 'Theatre A (Main)',
    hoursBooked: 136,
    hoursUsed: 115.6,
    utilization: 85,
    turnoverMin: 22,
    cases: 48,
  },
  {
    name: 'Theatre B',
    hoursBooked: 120,
    hoursUsed: 86.4,
    utilization: 72,
    turnoverMin: 28,
    cases: 35,
  },
  {
    name: 'Procedure Room 1',
    hoursBooked: 80,
    hoursUsed: 36.0,
    utilization: 45,
    turnoverMin: 15,
    cases: 22,
  },
];

const PEAK_HOURS = [
  { slot: '08:00 - 10:00', cases: 34, label: 'Peak' },
  { slot: '10:00 - 12:00', cases: 28, label: 'High' },
  { slot: '14:00 - 16:00', cases: 22, label: 'Moderate' },
  { slot: '16:00 - 18:00', cases: 12, label: 'Low' },
  { slot: '06:00 - 08:00', cases: 6, label: 'Low' },
];

const utilizationColor = (pct) =>
  pct >= 80 ? '#4CAF50' : pct >= 50 ? '#FF9800' : '#F44336';

// ── Component ────────────────────────────────────────────────────────────────
const TheatreUtilization = () => {
  const classes = useStyles();
  const [dateRange, setDateRange] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const totalCases = THEATRE_DATA.reduce((s, t) => s + t.cases, 0);
  const overallUtil = Math.round(
    THEATRE_DATA.reduce((s, t) => s + t.hoursUsed, 0) /
      THEATRE_DATA.reduce((s, t) => s + t.hoursBooked, 0) * 100
  );
  const avgCasesPerDay = Math.round(totalCases / 22);

  return (
    <div>
      <PageTitle title="Theatre Utilization" />
      <SubHeader title="Surgery" subTitle="Theatre Utilization" />

      <Paper className={classes.root} elevation={1}>
        {/* ── Date Range Filter ─────────────────────────────────────── */}
        <div className={classes.filterRow}>
          <Typography className={classes.filterLabel}>Period:</Typography>
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            variant="outlined"
            size="small"
            className={classes.filterSelect}
          >
            {DATE_RANGES.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </Select>
          {dateRange === 'custom' && (
            <>
              <TextField
                type="date"
                label="From"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
              <TextField
                type="date"
                label="To"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
            </>
          )}
        </div>

        {/* ── Summary Stats ─────────────────────────────────────────── */}
        <Grid container spacing={2} style={{ marginBottom: 24 }}>
          {[
            { label: 'Overall Utilization', value: `${overallUtil}%`, color: '#3F51B5' },
            { label: 'Total Cases', value: totalCases, color: '#009688' },
            { label: 'Avg Cases / Day', value: avgCasesPerDay, color: '#FF9800' },
          ].map((s) => (
            <Grid item xs={12} sm={4} key={s.label}>
              <Paper className={classes.summaryCard} style={{ backgroundColor: s.color }} elevation={2}>
                <Typography className={classes.summaryValue}>{s.value}</Typography>
                <Typography className={classes.summaryLabel}>{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ── Per-Theatre Table ──────────────────────────────────────── */}
        <Typography className={classes.sectionTitle}>Per-Theatre Utilization</Typography>
        <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 20 }}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Theatre</TableCell>
                <TableCell align="center">Hours Booked</TableCell>
                <TableCell align="center">Hours Used</TableCell>
                <TableCell style={{ minWidth: 180 }}>Utilization %</TableCell>
                <TableCell align="center">Turnover (avg min)</TableCell>
                <TableCell align="center">Cases</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {THEATRE_DATA.map((t) => (
                <TableRow key={t.name}>
                  <TableCell className={classes.tableCell}>{t.name}</TableCell>
                  <TableCell align="center" className={classes.tableCell}>{t.hoursBooked}</TableCell>
                  <TableCell align="center" className={classes.tableCell}>{t.hoursUsed}</TableCell>
                  <TableCell>
                    <div className={classes.utilizationBarBg}>
                      <div
                        className={classes.utilizationBarFill}
                        style={{
                          width: `${t.utilization}%`,
                          backgroundColor: utilizationColor(t.utilization),
                        }}
                      >
                        {t.utilization}%
                      </div>
                    </div>
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell}>{t.turnoverMin} min</TableCell>
                  <TableCell align="center" className={classes.tableCell}>{t.cases}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Peak Hours ────────────────────────────────────────────── */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography className={classes.sectionTitle}>Peak Hours</Typography>
            <Paper className={classes.sectionPaper} elevation={0} variant="outlined">
              {PEAK_HOURS.sort((a, b) => b.cases - a.cases).map((p) => (
                <div key={p.slot} className={classes.peakSlot}>
                  <Typography className={classes.peakTime}>{p.slot}</Typography>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Typography className={classes.peakCount}>{p.cases} cases</Typography>
                    <Chip
                      label={p.label}
                      size="small"
                      style={{
                        backgroundColor:
                          p.label === 'Peak' ? '#F44336' :
                          p.label === 'High' ? '#FF9800' :
                          p.label === 'Moderate' ? '#2196F3' : '#9E9E9E',
                        color: '#fff',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        height: 20,
                      }}
                    />
                  </div>
                </div>
              ))}
            </Paper>
          </Grid>

          {/* ── Cancellation Impact ────────────────────────────────── */}
          <Grid item xs={12} md={6}>
            <Typography className={classes.sectionTitle}>Cancellation Impact</Typography>
            <Paper className={classes.sectionPaper} elevation={0} variant="outlined">
              <div className={classes.cancellationRow}>
                <div className={classes.cancellationStat}>
                  <Typography className={classes.cancellationValue}>7</Typography>
                  <Typography className={classes.cancellationLabel}>Surgeries Cancelled</Typography>
                </div>
                <div className={classes.cancellationStat}>
                  <Typography className={classes.cancellationValue}>18.5 hrs</Typography>
                  <Typography className={classes.cancellationLabel}>Theatre Time Lost</Typography>
                </div>
                <div className={classes.cancellationStat}>
                  <Typography style={{ fontSize: '1.25rem', fontWeight: 700, color: '#FF9800' }}>
                    4.8%
                  </Typography>
                  <Typography className={classes.cancellationLabel}>Cancellation Rate</Typography>
                </div>
              </div>
              <Typography style={{ fontSize: '0.8rem', color: '#777', marginTop: 8 }}>
                Top reasons: Patient unfit (3), Scheduling conflict (2), Equipment unavailable (1), Patient no-show (1)
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

export default TheatreUtilization;
